import WebworkerPromise from 'webworker-promise';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import {
  startWorker, Workers,
} from '~/_shared/utils/worker/startWorker';
import type { ExportReportTemplateSelectorArea } from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import type { ExportReportAreaQuery } from '~/exportReport/validation/exportReportAreas.worker.helpers';

// Cached by the geometry reference only. `query` is a rest parameter so it does not count towards `fn.length`,
// which memoizee uses to decide which arguments form the cache key.
const getExportReportAreasMemoized = memoizeWeak(async (cacheKey: object, ...[query]: [ExportReportAreaQuery]): Promise<ReadonlyArray<ExportReportTemplateSelectorArea>> => {
  const worker = new WebworkerPromise(startWorker(Workers.GetExportReportAreas));

  return worker.postMessage(query)
    .catch((e) => {
      // Remove failed calculations from the cache
      getExportReportAreasMemoized.delete(cacheKey, ...[query]);
      throw e;
    })
    .finally(() => {
      worker.terminate();
    });
});

const removeHoles = (multiPolygon: MultiPolygon): MultiPolygon => multiPolygon.map(polygon => ({ path: polygon.path, holes: [] }));

// Holes are ignored - an area is supported when the outer paths are.
export const getExportReportAreas = (query: ExportReportAreaQuery): Promise<ReadonlyArray<ExportReportTemplateSelectorArea>> => (
  query.type === 'polygon'
    ? getExportReportAreasMemoized(query.paths, { type: 'polygon', paths: removeHoles(query.paths) })
    : getExportReportAreasMemoized(query.point, { type: 'point', point: { lat: query.point.lat, lng: query.point.lng } })
);
